// services/inventoryService.js
import baseService from "./baseService";
import { store } from "../Redux/store";

// Get token helper function
const getAuthHeaders = () => {
    const state = store.getState();
    const token = state.auth?.token;
    return token ? { headers: { "x-auth-token": token } } : {};
};

export const inventoryService = {
    // ===== MAIN INVENTORY SERVICES =====
    mainInventory: {
        // Create new main inventory (Admin only)
        create: async (data) => {
            try {
                const response = await baseService.post("/inventory/main-inventories", data, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        },

        // Get all main inventories with filters
        getAll: async (params = {}) => {
            try {
                const queryString = new URLSearchParams(params).toString();
                const response = await baseService.get(`/inventory/main-inventories?${queryString}`, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        },

        // Get single main inventory with items
        getById: async (id) => {
            try {
                const response = await baseService.get(`/inventory/main-inventories/${id}`, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        },

        // Update main inventory
        update: async (id, data) => {
            try {
                const response = await baseService.put(`/inventory/main-inventories/${id}`, data, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        },

        // Delete main inventory
        delete: async (id) => {
            try {
                const response = await baseService.delete(`/inventory/main-inventories/${id}`, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        },

        // Toggle favorite status
        toggleFavorite: async (id) => {
            try {
                const response = await baseService.patch(`/inventory/main-inventories/${id}/favorite`, {}, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        },

        // Get dashboard overview
        getDashboard: async () => {
            try {
                const response = await baseService.get("/inventory/main-inventories/dashboard", getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        }
    },

    // ===== INVENTORY ITEMS SERVICES =====
    items: {
        // Create new inventory item
        create: async (data) => {
            try {
                const response = await baseService.post("/inventory/inventory-items", data, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        },

        // Bulk create inventory items
        bulkCreate: async (data) => {
            try {
                const response = await baseService.post("/inventory/inventory-items/bulk", data, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        },

        // Get all inventory items across all inventories
        getAll: async (params = {}) => {
            try {
                const queryString = new URLSearchParams(params).toString();
                const response = await baseService.get(`/inventory/inventory-items?${queryString}`, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        },

        // Get inventory items by main inventory ID
        getByMainInventoryId: async (mainInventoryId, params = {}) => {
            try {
                const queryString = new URLSearchParams(params).toString();
                const response = await baseService.get(`/inventory/inventory-items/main/${mainInventoryId}?${queryString}`, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        },

        // Get single inventory item
        getById: async (id) => {
            try {
                const response = await baseService.get(`/inventory/inventory-items/${id}`, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        },

        // Update inventory item
        update: async (id, data) => {
            try {
                const response = await baseService.put(`/inventory/inventory-items/${id}`, data, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        },

        // Update inventory item quantity
        updateQuantity: async (id, data) => {
            try {
                const response = await baseService.patch(`/inventory/inventory-items/${id}/quantity`, data, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        },

        // Delete inventory item
        delete: async (id) => {
            try {
                const response = await baseService.delete(`/inventory/inventory-items/${id}`, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        }
    },

    // ===== INVENTORY TRANSFER SERVICES =====
    transfers: {
        // Transfer items between inventories
        create: async (data) => {
            try {
                const response = await baseService.post("/inventory/inventory-transfers", data, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        },

        // Get transfer history
        getHistory: async (params = {}) => {
            try {
                const queryString = new URLSearchParams(params).toString();
                const response = await baseService.get(`/inventory/inventory-transfers?${queryString}`, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        },

        getById: async (id) => {
            try {
                const response = await baseService.get(`/inventory/inventory-transfers/${id}`, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : error.message;
            }
        },
        inventoryToProject: async (data) => {
            try {
                const response = await baseService.post("/inventory/inventory-transfers/inventory-to-project", data, getAuthHeaders());
                return response.data;
            } catch (error) {
                return error.response?.data ? error.response?.data : { success: false, message: error.message };
            }
        },
    },
}